import type { CSSProperties } from 'react';
import { useEffect } from 'react';
import { useBranding } from '@/hooks/useBranding';

interface RestaurantLogoProps {
  /** Override width in px; defaults to system_config.logo_size */
  width?: number;
  /** Max height cap for compact headers (px) */
  maxHeight?: number;
  className?: string;
  style?: CSSProperties;
  alt?: string;
  /**
   * true = koyu arka plan üzerinde (beyaz+kırmızı logo)
   * false = açık arka plan üzerinde (koyu+kırmızı logo)
   */
  invert?: boolean;
  showPlaceholder?: boolean;
}

const RestaurantLogo = ({
  width,
  maxHeight,
  className = '',
  style,
  alt,
  invert = false,
  showPlaceholder = true,
}: RestaurantLogoProps) => {
  const { logoOnDarkUrl, logoOnLightUrl, logoSize, restaurantName } = useBranding();
  const w = width ?? logoSize;
  const resolvedAlt = alt ?? restaurantName;
  const src = invert ? logoOnDarkUrl : logoOnLightUrl;

  // Invert / sayfa geçişlerinde flash olmasın diye her iki varyantı ön yükle
  useEffect(() => {
    [logoOnDarkUrl, logoOnLightUrl].forEach((url) => {
      if (!url) return;
      const img = new Image();
      img.src = url;
    });
  }, [logoOnDarkUrl, logoOnLightUrl]);

  if (!src) {
    if (!showPlaceholder) return null;
    return (
      <div
        className={`flex items-center justify-center rounded-xl bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/20 flex-shrink-0 ${className}`}
        style={{ width: Math.min(w, 56), height: Math.min(w * 0.45, maxHeight ?? 40), ...style }}
        aria-label={resolvedAlt}
      >
        <i className="ri-fire-line text-red-500 text-xl" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={resolvedAlt}
      className={`object-contain flex-shrink-0 ${className}`}
      decoding="async"
      fetchPriority="high"
      style={{
        width: 'auto',
        maxWidth: `${w}px`,
        height: maxHeight ? `${maxHeight}px` : 'auto',
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        ...style,
      }}
    />
  );
};

export default RestaurantLogo;
